import React, { useState, Children } from 'react';
import { useTranslation } from 'react-i18next';
import { Reservation } from '../../../shared/types/Reservation';
import { SearchIcon, FilterIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
export function Reservations() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [reservations, setReservations] = useState<Reservation[]>([{
    id: '1',
    pnr: '3Y6TE994',
    dateCreated: '27 March 2026 at 17:14',
    checkInDate: '09 Apr 2026',
    checkOutDate: '10 Apr 2026',
    mainGuest: 'Simon Verraest',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    },
    total: formatCurrencyAmount(1.29, {
      fractionDigits: 2
    }),
    status: 'confirmed'
  }, {
    id: '2',
    pnr: 'FRUJDGE',
    dateCreated: '26 March 2026 at 17:08',
    checkInDate: '10 May 2026',
    checkOutDate: '11 May 2026',
    mainGuest: 'Ahmed Waleed',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    },
    total: formatCurrencyAmount(1.29, {
      fractionDigits: 2
    }),
    status: 'confirmed'
  }, {
    id: '3',
    pnr: 'DERUVIM34',
    dateCreated: '25 March 2026 at 19:15',
    checkInDate: '28 Mar 2026',
    checkOutDate: '29 Mar 2026',
    mainGuest: 'Simon Verraest',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    },
    total: formatCurrencyAmount(1.29, {
      fractionDigits: 2
    }),
    status: 'confirmed'
  }]);
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Filter reservations based on search term and status filter
  const filteredReservations = reservations.filter(reservation => {
    const matchesSearch = reservation.mainGuest.toLowerCase().includes(searchTerm.toLowerCase()) || reservation.pnr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reservation.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Format guest count for display
  function formatGuestCount(guests: {
    adults: number;
    children: number;
    infants: number;
  }): string {
    return `${guests.adults} Adults, ${guests.children} Children, ${guests.infants} Infants`;
  }
  // Navigate to reservation detail
  const navigateToDetail = (pnr: string) => {
    window.location.href = `/reservation-detail?pnr=${pnr}`;
  };
  return <div className="space-y-6" data-id="element-9196">
      <div className="flex justify-between items-center" data-id="element-9197">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-9198">
          {t('pages.Reservations.reservations')}
        </h1>
      </div>
      {/* Search and filters */}
      <div className="flex flex-wrap gap-3" data-id="element-9199">
        <div className="relative flex-grow max-w-xs" data-id="element-9200">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-9201">
            <SearchIcon size={16} className="text-gray-400" data-id="element-9202" />
          </div>
          <input type="search" aria-label={t('pages.Reservations.attrs.search_reservations')} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md" placeholder={t('pages.Reservations.attrs.search_reservations')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-9203" />
        </div>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-9204">
          <option value="all" data-id="element-9205">{t('components.AddRoomForm.status')}</option>
          <option value="confirmed" data-id="element-9206">
            {t('pages.ReservationDetail.confirmed')}
          </option>
          <option value="cancelled" data-id="element-9207">{t('pages.Reservations.cancelled')}</option>
          <option value="pending" data-id="element-9208">{t('pages.Reservations.pending')}</option>
        </select>
      </div>
      {/* Reservations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto" data-id="element-9209">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-9210">
          <thead className="bg-gray-50" data-id="element-9211">
            <tr data-id="element-9212">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9213">
                {t('pages.Reservations.date_created')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9214">
                {t('pages.Reservations.pnr')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9215">
                {t('pages.Reservations.checkin_date')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9216">
                {t('pages.Reservations.checkout_date')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9217">
                {t('pages.Reservations.main_guest')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9218">
                {t('pages.Reservations.guests')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9219">
                {t('pages.ReservationDetail.total')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-9220">
            {filteredReservations.map(reservation => <tr key={reservation.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigateToDetail(reservation.pnr)} data-id="element-9221">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9222">
                  {reservation.dateCreated}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-9223">
                  {reservation.pnr}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9224">
                  {reservation.checkInDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9225">
                  {reservation.checkOutDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-9226">
                  {reservation.mainGuest}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9227">
                  {formatGuestCount(reservation.guests)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-9228">
                  {reservation.total}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}